'use client';

import React from 'react';
import {
    Wallet,
    CheckCircle2,
    AlertCircle,
    Calendar,
    ArrowRight,
    GraduationCap,
    School,
    CreditCard,
    Receipt,
    Clock,
    History,
    LayoutDashboard
} from 'lucide-react';

export default function WalimuridDashboard() {
    // Mock data based on selected child (e.g., Ahmad Al-Fatih)
    const activeStudent = {
        name: 'Ahmad Al-Fatih',
        school: 'SD Swasta Pekanbaru',
        class: 'Kelas 4A - Tahfidz',
        academicYear: '2024 / 2025 (Ganjil)',
        studentId: '2024098711'
    };

    const financialSummary = {
        totalActiveBills: 4500000,
        totalPaid: 3000000,
        remainingBalance: 1500000,
        currentMonthStatus: 'Paid', // or 'Unpaid', 'Pending'
        nextDueDate: '10 Februari 2025'
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="max-w-7xl mx-auto w-full space-y-10 animate-in fade-in duration-700">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 min-h-[80px]">
                <div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight flex items-center gap-4">
                        <LayoutDashboard className="w-8 h-8 text-primary" /> Dashboard Utama
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 font-medium">Ikhtisar aktivitas dan status keuangan pendidikan anak Anda.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="bg-surface border border-border-main px-6 py-3 rounded-2xl flex items-center gap-3 shadow-xs">
                        <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                        <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Sistem Terhubung</p>
                    </div>
                </div>
            </div>

            {/* 1. Informasi Anak Aktif Card */}
            <div className="bg-linear-to-r from-primary to-primary-dark rounded-[40px] p-8 md:p-10 text-white shadow-2xl shadow-primary/20 relative overflow-hidden">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="flex items-center gap-6">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-xl rounded-[28px] flex items-center justify-center border border-white/30 shadow-inner">
                            <GraduationCap className="w-10 h-10 text-white" />
                        </div>
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-white/70 uppercase tracking-[0.2em]">Profil Siswa Aktif</p>
                            <h2 className="text-3xl font-black tracking-tight">{activeStudent.name}</h2>
                            <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-white/80">
                                <span className="flex items-center gap-1.5"><School className="w-3.5 h-3.5" /> {activeStudent.school}</span>
                                <span className="w-1 h-1 bg-white/40 rounded-full"></span>
                                <span className="flex items-center gap-1.5 font-bold text-white">{activeStudent.class}</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20">
                        <p className="text-[9px] font-bold text-white/60 uppercase tracking-widest mb-1">Tahun Ajaran</p>
                        <p className="text-sm font-bold flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-white/70" /> {activeStudent.academicYear}
                        </p>
                    </div>
                </div>
                {/* Decorative Elements */}
                <div className="absolute top-[-10%] right-[-5%] w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-[-20%] left-[10%] w-48 h-48 bg-primary-light/10 rounded-full blur-2xl"></div>
            </div>

            {/* 2. Card Summary Financial */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total Tagihan */}
                <div className="bg-surface border border-border-main p-8 rounded-[32px] shadow-xs group hover:border-primary/50 transition-all">
                    <div className="w-12 h-12 rounded-2xl bg-primary-soft/50 flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform">
                        <Receipt className="w-6 h-6" />
                    </div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Total Tagihan Aktif</p>
                    <h3 className="text-xl font-black text-foreground">{formatCurrency(financialSummary.totalActiveBills)}</h3>
                </div>

                {/* Sudah Dibayar */}
                <div className="bg-surface border border-border-main p-8 rounded-[32px] shadow-xs group hover:border-success/50 transition-all">
                    <div className="w-12 h-12 rounded-2xl bg-success-soft/50 flex items-center justify-center text-success mb-6 group-hover:scale-110 transition-transform">
                        <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Total Sudah Dibayar</p>
                    <h3 className="text-xl font-black text-foreground">{formatCurrency(financialSummary.totalPaid)}</h3>
                </div>

                {/* Sisa Tagihan */}
                <div className="bg-surface border border-border-main p-8 rounded-[32px] shadow-xs group hover:border-error/50 transition-all">
                    <div className="w-12 h-12 rounded-2xl bg-error/5 flex items-center justify-center text-error mb-6 group-hover:scale-110 transition-transform">
                        <Wallet className="w-6 h-6" />
                    </div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Sisa Tagihan</p>
                    <h3 className="text-xl font-black text-error">{formatCurrency(financialSummary.remainingBalance)}</h3>
                </div>

                {/* Status Bulan Ini */}
                <div className="bg-primary-soft/30 border-2 border-primary/10 p-8 rounded-[32px] shadow-xs flex flex-col justify-between">
                    <div>
                        <p className="text-[10px] font-bold text-primary uppercase tracking-widest mb-1.5 flex items-center gap-2">
                            <Clock className="w-3 h-3" /> Status Februari
                        </p>
                        <h3 className="text-2xl font-black text-primary">Lunas</h3>
                    </div>
                    <p className="text-[10px] text-primary/60 font-medium mt-4">Terima kasih atas pembayaran tepat waktu Anda.</p>
                </div>
            </div>

            {/* 3. Quick Action & Information */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Information Card (Garis Besar) */}
                <div className="lg:col-span-2 bg-surface rounded-[40px] border border-border-main p-10 shadow-xs space-y-8">
                    <div className="flex items-center justify-between border-b border-border-main pb-6">
                        <h2 className="text-xl font-bold flex items-center gap-3">
                            <AlertCircle className="w-6 h-6 text-primary" /> Informasi Penting
                        </h2>
                    </div>
                    <div className="space-y-6">
                        <div className="flex items-start gap-5 p-6 bg-background rounded-[28px] border border-border-main border-dashed">
                            <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                                <CreditCard className="w-5 h-5 text-primary" />
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-bold text-foreground tracking-tight">Belum Ada Tagihan Baru</p>
                                <p className="text-xs text-muted-foreground leading-relaxed font-medium">Tagihan bulan Maret akan muncul pada tanggal 25 Februari. Pastikan saldo Anda mencukupi jika menggunakan fitur Auto-Debit.</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-5 p-6 bg-background rounded-[28px] border border-border-main border-dashed">
                            <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                                <History className="w-5 h-5 text-primary" />
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-bold text-foreground tracking-tight">Konfirmasi Pembayaran Otomatis</p>
                                <p className="text-xs text-muted-foreground leading-relaxed font-medium">Sistem kami sudah terhubung dengan perbankan. Anda tidak perlu mengunggah bukti transfer jika menggunakan Kode Virtual Account.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Action: Bayar Sekarang */}
                <div className="bg-linear-to-b from-surface to-background rounded-[40px] border border-border-main p-10 shadow-xs flex flex-col items-center justify-center text-center space-y-6">
                    <div className="w-20 h-20 bg-primary-soft rounded-full flex items-center justify-center text-primary shadow-inner">
                        <CreditCard className="w-10 h-10" />
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-xl font-bold">Bayar Tagihan Sisa</h3>
                        <p className="text-xs text-muted-foreground font-medium px-4">Selesaikan semua tagihan tersisa anak Anda dengan satu kali transaksi.</p>
                    </div>
                    <div className="text-2xl font-black text-primary py-2">
                        {formatCurrency(financialSummary.remainingBalance)}
                    </div>
                    <button className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 rounded-2xl shadow-xl shadow-primary/20 flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-95 group">
                        Bayar Sekarang <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <p className="text-[10px] text-muted-foreground font-medium">
                        Batas akhir: <span className="font-bold text-foreground">{financialSummary.nextDueDate}</span>
                    </p>
                </div>
            </div>
        </div>
    );
}
